import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import {
  useTasksByAssignee,
  useTasksUnassigned,
  useTasksToValidate,
  useMyActiveTags,
  useMyAssignees,
  useTasksByTag,
  useMyTagsForTasks,
  useRemindersByTag,
  toggleReminderDone,
  deleteReminder,
  useAssigneeReadReceipts,
  useMyThreads,
  markThreadRead,
  markThreadUnread,
  useTeamStages,
} from './api';
import type { MyThread } from './api';
import TaskCard from './TaskCard';
import BoardView from './BoardView';
import { ReminderCard, ReminderForm } from './AgendaPage';
import InboxEmpty from './InboxEmpty';
import NuevoButton from './NuevoButton';
import FilterDropdown from './FilterDropdown';
import type { FilterOption } from './FilterDropdown';
import { useDialog } from '../ui/Dialogs';
import type { Reminder, Task } from './types';

type FiltroKey = 'todas' | 'en_curso' | 'terminada';

const FILTROS: Array<{ key: FiltroKey; label: string }> = [
  { key: 'todas', label: 'Todas' },
  { key: 'en_curso', label: 'En curso' },
  { key: 'terminada', label: 'Terminadas' },
];

type LecturaKey = 'todas' | 'no_leidas' | 'leidas';

// Filtro de participación de la vista "Todo": por qué vía estoy en el hilo.
type PartKey = 'todas' | 'a_mi' | 'por_mi' | 'menciones' | 'siguiendo';
const PART_KEYS: PartKey[] = ['todas', 'a_mi', 'por_mi', 'menciones', 'siguiendo'];
const PART_LABEL: Record<PartKey, string> = {
  todas: 'Todo',
  a_mi: 'A mí',
  por_mi: 'Por mí',
  menciones: 'Menciones',
  siguiendo: 'Siguiendo',
};
const PART_SUBTITLE: Record<PartKey, string> = {
  todas: 'Todo lo tuyo: lo que te asignan, lo que asignas, donde te mencionan y lo que sigues. Las novedades sin leer, arriba.',
  a_mi: 'Hilos asignados a ti.',
  por_mi: 'Hilos que has abierto tú, se los hayas asignado a quien sea.',
  menciones: 'Hilos donde te han mencionado.',
  siguiendo: 'Hilos a los que te han añadido como seguidor.',
};
function matchesPart(t: MyThread, part: PartKey): boolean {
  switch (part) {
    case 'a_mi': return t.is_assignee;
    case 'por_mi': return t.is_creator;
    case 'menciones': return t.is_mentioned;
    case 'siguiendo': return t.is_follower;
    default: return true;
  }
}

// Lista o tablero (kanban): preferencia por navegador, común a todas las vistas.
type ViewMode = 'lista' | 'tablero';
const VIEW_KEY = 'bk_bandeja_view';
function readViewMode(): ViewMode {
  try {
    return localStorage.getItem(VIEW_KEY) === 'tablero' ? 'tablero' : 'lista';
  } catch {
    return 'lista';
  }
}

export default function BandejaPage() {
  const { profile } = useAuth();
  const { confirm } = useDialog();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const asignado = params.get('asignado');
  const tagId = params.get('tag');
  const vista = params.get('vista');
  const isGeneral = vista === 'general';
  const isToValidate = vista === 'por_validar';
  const isTag = !!tagId;
  // Vista personal "Todo": todos los hilos en los que participo (asignado a
  // mí, creado por mí, mencionado o seguidor), con no leídos arriba. Es la
  // Bandeja por defecto; las antiguas pestañas Menciones/Siguiendo son ahora
  // filtros de participación (y sus URLs ?vista= siguen funcionando).
  const isTodo = !isToValidate && !isGeneral && !isTag && !asignado;
  const fParam = params.get('f');
  const part: PartKey = (PART_KEYS as string[]).includes(fParam ?? '')
    ? (fParam as PartKey)
    : vista === 'menciones'
      ? 'menciones'
      : vista === 'siguiendo'
        ? 'siguiendo'
        : 'todas';
  const setPart = (k: PartKey) => navigate(k === 'todas' ? '/bandeja' : `/bandeja?f=${k}`);

  const myThreads = useMyThreads(profile?.id);
  const assigneeResult = useTasksByAssignee(asignado || '', profile?.id || '');
  const generalResult = useTasksUnassigned();
  const toValidateResult = useTasksToValidate(profile?.id);
  const tagResult = useTasksByTag(tagId || '');
  const { assignees } = useMyAssignees(profile?.id);
  const { tags: activeTags } = useMyActiveTags(profile?.id);

  // En "Todo" la lista viene de la RPC ya filtrada por participación y con
  // los no leídos primero (dentro de cada bloque, por última actividad).
  const todoTasks = useMemo<MyThread[]>(() => {
    if (!isTodo) return [];
    const list = myThreads.threads.filter((t) => matchesPart(t, part));
    return [...list].sort((a, b) => Number(b.unread) - Number(a.unread));
  }, [isTodo, myThreads.threads, part]);
  const unreadById = useMemo(() => {
    const m: Record<string, MyThread> = {};
    myThreads.threads.forEach((t) => { m[t.id] = t; });
    return m;
  }, [myThreads.threads]);

  const { tasks, loading } = isTodo
    ? { tasks: todoTasks as Task[], loading: myThreads.loading }
    : isToValidate
      ? toValidateResult
      : isGeneral
        ? generalResult
        : isTag
          ? tagResult
          : assigneeResult;

  const tagInfo = isTag ? activeTags.find((t) => t.id === tagId) : null;

  // En vista TAG, además de los hilos, traemos mis recordatorios con esa tag
  // para unificarlos como en la Agenda.
  const tagReminders = useRemindersByTag(tagId || '', profile?.id);
  const [reminderForm, setReminderForm] = useState<Reminder | null>(null);

  const [filtro, setFiltro] = useState<FiltroKey>('en_curso');
  // El tablero no aplica en "Por validar" (todo está resuelto) ni en la vista
  // de etiqueta (mezcla hilos con recordatorios).
  const canBoard = !isToValidate && !isTag;
  const [viewMode, setViewModeState] = useState<ViewMode>(readViewMode);
  const setViewMode = (m: ViewMode) => {
    setViewModeState(m);
    try { localStorage.setItem(VIEW_KEY, m); } catch { /* sin localStorage: no se recuerda */ }
  };
  const isBoard = canBoard && viewMode === 'tablero';
  const { stages } = useTeamStages();
  const stageById = useMemo(() => new Map(stages.map((st) => [st.id, st])), [stages]);
  const [lectura, setLectura] = useState<LecturaKey>('todas');
  // Filtros cruzados: en vista TAG se puede filtrar por usuario, en vista
  // assignee por etiqueta. Solo aplican en su vista correspondiente.
  const [crossUserId, setCrossUserId] = useState<string | null>(null);
  const [crossTagId, setCrossTagId] = useState<string | null>(null);
  // Filtro "asignado por" en la vista personal (Todo): quién ha abierto cada
  // hilo, no a quién se lo has asignado tú.
  const [crossCreatorId, setCrossCreatorId] = useState<string | null>(null);

  // Tags MÍAS aplicadas a los hilos cargados: para los chips de cada tarjeta
  // (todas las vistas) y para poblar el dropdown de "Etiqueta" en vista assignee.
  const taskIds = useMemo(() => tasks.map((t) => t.id), [tasks]);
  const { tagsByTask } = useMyTagsForTasks(taskIds, profile?.id);

  // Acuse de lectura del asignado (tick gris/verde en la tarjeta). Solo aplica a
  // hilos asignados a OTRA persona; los míos o los de #General no llevan tick.
  const receipts = useAssigneeReadReceipts(tasks, profile?.id);

  // Hilos ya recortados por estado (En curso/Terminadas) y lectura, pero SIN
  // los filtros cruzados (persona/etiqueta/asignador) — es la base sobre la que
  // se construyen las opciones de esos dropdowns, para no ofrecer gente que no
  // tiene ningún hilo en la pestaña de estado actual.
  const estadoFiltered = useMemo(() => {
    // En "Por validar" todos los hilos están en estado resuelto: no aplicamos
    // el filtro de estado (En curso / Terminadas). En el tablero tampoco: el
    // estado lo dan las columnas.
    let out = isToValidate || isBoard || filtro === 'todas' ? tasks : tasks.filter((t) => t.estado === filtro);
    if (isTodo && lectura !== 'todas') {
      out = out.filter((t) => {
        const unread = unreadById[t.id]?.unread ?? false;
        return lectura === 'no_leidas' ? unread : !unread;
      });
    }
    return out;
  }, [tasks, filtro, isToValidate, isBoard, isTodo, lectura, unreadById]);

  // Options para los dropdowns
  const userOptions: FilterOption[] = useMemo(() => {
    if (!isTag) return [];
    const map = new Map<string, FilterOption>();
    estadoFiltered.forEach((t) => {
      if (t.assignee) {
        map.set(t.assignee.id, {
          id: t.assignee.id,
          label: t.assignee.nombre,
          color: t.assignee.color,
        });
      }
    });
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [isTag, estadoFiltered]);

  const tagOptions: FilterOption[] = useMemo(() => {
    if (!asignado || isTag) return [];
    const ids = new Set(estadoFiltered.map((t) => t.id));
    const map = new Map<string, FilterOption>();
    Object.entries(tagsByTask).forEach(([taskId, list]) => {
      if (!ids.has(taskId)) return;
      list.forEach((tg) => {
        map.set(tg.id, { id: tg.id, label: tg.nombre, color: tg.color });
      });
    });
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [asignado, isTag, estadoFiltered, tagsByTask]);

  // Opciones para "Asignado por": solo en la vista personal (Todo), donde
  // cada hilo puede venir de una persona distinta.
  const isPersonalView = isTodo;
  const creatorOptions: FilterOption[] = useMemo(() => {
    if (!isPersonalView) return [];
    const map = new Map<string, FilterOption>();
    estadoFiltered.forEach((t) => {
      if (t.creator) {
        map.set(t.creator.id, {
          id: t.creator.id,
          label: t.creator.nombre,
          color: t.creator.color,
        });
      }
    });
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [isPersonalView, estadoFiltered]);

  // Al cambiar de filtro de participación el "asignado por" deja de tener
  // sentido si esa persona no tiene hilos ahí, así que se resetea.
  useEffect(() => { setCrossCreatorId(null); }, [part]);

  const filtradas = useMemo(() => {
    let out = estadoFiltered;
    if (isTag && crossUserId) {
      out = out.filter((t) => t.assignee?.id === crossUserId);
    }
    if (asignado && !isTag && crossTagId) {
      out = out.filter((t) => (tagsByTask[t.id] ?? []).some((tg) => tg.id === crossTagId));
    }
    if (isPersonalView && crossCreatorId) {
      out = out.filter((t) => t.creator?.id === crossCreatorId);
    }
    return out;
  }, [estadoFiltered, isTag, crossUserId, asignado, crossTagId, tagsByTask, isPersonalView, crossCreatorId]);

  // Recordatorios de la tag, sujetos al mismo filtro de estado que los hilos:
  // En curso = no hechos, Terminadas = hechos. Al filtrar por persona se ocultan
  // (un recordatorio es personal, no tiene asignado).
  const filtReminders = useMemo(() => {
    if (!isTag || crossUserId) return [];
    return tagReminders.reminders.filter((r) =>
      filtro === 'todas' ? true : filtro === 'en_curso' ? !r.done : r.done,
    );
  }, [isTag, crossUserId, tagReminders.reminders, filtro]);

  // El nombre/color del compañero los sacamos del propio listado de assignees
  // (no de useTeam) — coherente con la privacidad: si la persona ya no aparece
  // en mi TEAM, tampoco quiero mostrar su nombre coloreado en este encabezado.
  const persona = asignado ? assignees.find((a) => a.profile.id === asignado)?.profile ?? null : null;

  // Chips de participación de la vista "Todo". El badge de cada uno cuenta
  // los hilos en curso con novedades sin leer por esa vía.
  const partBadges = useMemo(() => {
    const out: Record<PartKey, number> = { todas: 0, a_mi: 0, por_mi: 0, menciones: 0, siguiendo: 0 };
    myThreads.threads.forEach((t) => {
      if (!t.unread || t.estado !== 'en_curso') return;
      PART_KEYS.forEach((k) => { if (matchesPart(t, k)) out[k] += 1; });
    });
    return out;
  }, [myThreads.threads]);

  const unreadIds = useMemo(
    () => new Set(myThreads.threads.filter((t) => t.unread).map((t) => t.id)),
    [myThreads.threads],
  );

  const toggleThreadRead = async (t: MyThread) => {
    if (!profile) return;
    if (t.unread) {
      await markThreadRead(t.id, profile.id);
    } else if (t.last_foreign_at) {
      await markThreadUnread(t.id, profile.id, t.last_foreign_at);
    }
    myThreads.refetch();
  };

  return (
    <div style={isBoard ? styles.wrapBoard : styles.wrap}>
      <header style={styles.header}>
        <div style={{ minWidth: 0 }}>
          {isTodo ? (
            <div style={styles.tabs}>
              {PART_KEYS.map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setPart(k)}
                  style={{ ...styles.tab, ...(part === k ? styles.tabActive : null) }}
                >
                  <span>{PART_LABEL[k]}</span>
                  {partBadges[k] ? (
                    <span style={{ ...styles.tabBadge, ...(part === k ? styles.tabBadgeActive : null) }}>
                      {partBadges[k]}
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
          ) : (
            <h1 style={styles.h1}>
              {isToValidate
                ? <span style={{ color: '#b45309' }}>Por validar</span>
                : isGeneral
                  ? <span style={{ color: '#64748b' }}>#General</span>
                  : isTag
                    ? <>Etiqueta <span style={{ color: tagInfo?.color ?? '#1d4ed8' }}>{tagInfo?.nombre ?? '…'}</span></>
                    : persona
                      ? <>Hilos de <span style={{ color: persona.color }}>#{persona.nombre}</span></>
                      : 'Bandeja'}
            </h1>
          )}
          <p style={styles.subtitle}>
            {isTodo
              ? PART_SUBTITLE[part]
              : isToValidate
                ? 'Revisa los hilos resueltos. Puedes cerrarlos o reactivarlos.'
                : isGeneral
                  ? 'Hilos sin asignar — visibles para todo el equipo.'
                  : isTag
                    ? 'Hilos y recordatorios a los que has aplicado esta etiqueta.'
                    : persona
                      ? 'Hilos asignados a este miembro del equipo.'
                      : 'Bandeja'}
          </p>
        </div>
        <NuevoButton
          onHilo={() => navigate('/nueva')}
          onRecordatorio={() => navigate('/agenda?nuevo=recordatorio')}
        />
      </header>

      {!isToValidate && (
      <div style={styles.chips}>
        <div style={styles.chipsLeft}>
          {canBoard && (
            <div style={styles.viewToggle}>
              {(['lista', 'tablero'] as ViewMode[]).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setViewMode(m)}
                  style={{ ...styles.viewBtn, ...(viewMode === m ? styles.viewBtnActive : null) }}
                >
                  {m === 'lista' ? 'Lista' : 'Tablero'}
                </button>
              ))}
            </div>
          )}
          {!isBoard && FILTROS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFiltro(f.key)}
              style={{
                ...styles.chip,
                ...(filtro === f.key ? styles.chipActive : null),
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        {isTodo && (
          <div style={styles.chipsRight}>
            <button
              type="button"
              title={lectura === 'no_leidas' ? 'Quitar filtro (ver todas)' : 'Solo no leídas'}
              onClick={() => setLectura(lectura === 'no_leidas' ? 'todas' : 'no_leidas')}
              style={{ ...styles.chip, ...(lectura === 'no_leidas' ? styles.chipActive : null) }}
            >
              No leídas
            </button>
            <button
              type="button"
              title={lectura === 'leidas' ? 'Quitar filtro (ver todas)' : 'Solo leídas'}
              onClick={() => setLectura(lectura === 'leidas' ? 'todas' : 'leidas')}
              style={{ ...styles.chip, ...(lectura === 'leidas' ? styles.chipActive : null) }}
            >
              Leídas
            </button>
          </div>
        )}

        {isTag && userOptions.length > 0 && (
          <div style={styles.chipsRight}>
            <FilterDropdown
              label="Usuario"
              options={userOptions}
              selected={crossUserId}
              onChange={setCrossUserId}
            />
          </div>
        )}

        {asignado && !isTag && tagOptions.length > 0 && (
          <div style={styles.chipsRight}>
            <FilterDropdown
              label="Etiqueta"
              options={tagOptions}
              selected={crossTagId}
              onChange={setCrossTagId}
              allLabel="Todas"
            />
          </div>
        )}

        {isPersonalView && creatorOptions.length > 0 && (
          <div style={styles.chipsRight}>
            <FilterDropdown
              label="Asignado por"
              options={creatorOptions}
              selected={crossCreatorId}
              onChange={setCrossCreatorId}
              allLabel="Todos"
            />
          </div>
        )}
      </div>
      )}

      {loading || (isTag && tagReminders.loading) ? (
        <p style={styles.muted}>Cargando…</p>
      ) : filtradas.length === 0 && filtReminders.length === 0 ? (
        isTodo && part === 'todas' && lectura === 'todas' && !crossCreatorId ? <InboxEmpty /> : (
          <p style={styles.muted}>
            {isTag
              ? 'No hay hilos ni recordatorios con esta etiqueta.'
              : isTodo && lectura === 'no_leidas'
                ? 'Nada sin leer. Estás al día.'
                : 'No hay hilos en esta vista.'}
          </p>
        )
      ) : isBoard ? (
        <BoardView
          tasks={filtradas}
          stages={stages}
          userId={profile?.id}
          tagsByTask={tagsByTask}
          receipts={receipts}
          unreadIds={isTodo ? unreadIds : undefined}
          onChanged={isTodo ? myThreads.refetch : undefined}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {filtradas.map((t) => {
            if (isTodo) {
              const mt = unreadById[t.id];
              const unread = mt?.unread ?? false;
              // Sin novedades ajenas (solo he escrito yo) no hay nada que
              // "volver a marcar como no leído": el botón se deshabilita.
              const canUnread = !!mt?.last_foreign_at;
              return (
                <div key={t.id} style={styles.mentionRow}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <TaskCard task={t} tags={tagsByTask[t.id]} readReceipt={receipts[t.id]} unread={unread} stage={stageById.get(t.stage_id ?? '')} />
                  </div>
                  <button
                    type="button"
                    onClick={() => mt && toggleThreadRead(mt)}
                    disabled={!unread && !canUnread}
                    title={unread ? 'Marcar como leído' : canUnread ? 'Marcar como no leído' : 'Sin novedades de otros'}
                    style={{
                      ...styles.readToggle,
                      color: unread ? '#1d4ed8' : '#cbd5e1',
                      cursor: !unread && !canUnread ? 'default' : 'pointer',
                    }}
                  >
                    {unread ? '●' : '○'}
                  </button>
                </div>
              );
            }
            return <TaskCard key={t.id} task={t} tags={tagsByTask[t.id]} readReceipt={receipts[t.id]} stage={stageById.get(t.stage_id ?? '')} />;
          })}
          {filtReminders.map((r) => (
            <ReminderCard
              key={`r-${r.id}`}
              reminder={r}
              overdue={!r.done && r.fecha_vencimiento < localTodayStr()}
              done={r.done}
              onToggle={async () => { await toggleReminderDone(r.id, !r.done); tagReminders.refetch(); }}
              onEdit={() => setReminderForm(r)}
              onDelete={async () => {
                const ok = await confirm({ title: 'Borrar recordatorio', message: '¿Seguro que quieres borrar este recordatorio?', confirmLabel: 'Borrar', danger: true });
                if (ok) { await deleteReminder(r.id); tagReminders.refetch(); }
              }}
            />
          ))}
        </div>
      )}

      {reminderForm && (
        <ReminderForm
          initial={reminderForm}
          onClose={() => setReminderForm(null)}
          onSaved={() => { setReminderForm(null); tagReminders.refetch(); }}
          userId={profile?.id || ''}
        />
      )}
    </div>
  );
}

// Fecha local de hoy como 'YYYY-MM-DD' (las fechas de vencimiento son DATE).
function localTodayStr(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

const styles: Record<string, CSSProperties> = {
  wrap: { padding: '32px 40px', maxWidth: 920, margin: '0 auto' },
  wrapBoard: { padding: '32px 40px' },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
    marginBottom: 18,
  },
  h1: { fontSize: 26, fontWeight: 700, color: '#0f172a', margin: 0 },
  subtitle: { color: '#64748b', fontSize: 14, margin: '8px 0 0' },
  tabs: {
    display: 'flex',
    gap: 4,
    background: '#f1f5f9',
    padding: 4,
    borderRadius: 12,
    width: 'fit-content',
  },
  tab: {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    background: 'transparent',
    border: 'none',
    borderRadius: 9,
    padding: '8px 16px',
    fontSize: 15,
    fontWeight: 600,
    color: '#64748b',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  tabActive: {
    background: '#fff',
    color: '#0f172a',
    boxShadow: '0 1px 2px rgba(15,23,42,0.08)',
  },
  tabBadge: {
    background: '#e2e8f0',
    color: '#475569',
    fontSize: 11,
    fontWeight: 700,
    padding: '1px 7px',
    borderRadius: 999,
    minWidth: 18,
    textAlign: 'center',
  },
  tabBadgeActive: {
    background: '#eff5ff',
    color: '#2563eb',
  },
  chips: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
    marginBottom: 20,
  },
  chipsLeft: { display: 'flex', gap: 8, alignItems: 'center' },
  viewToggle: {
    display: 'flex',
    gap: 2,
    background: '#f1f5f9',
    padding: 3,
    borderRadius: 999,
    marginRight: 6,
  },
  viewBtn: {
    background: 'transparent',
    border: 'none',
    borderRadius: 999,
    padding: '4px 12px',
    fontSize: 13,
    fontWeight: 600,
    color: '#64748b',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  viewBtnActive: {
    background: '#fff',
    color: '#0f172a',
    boxShadow: '0 1px 2px rgba(15,23,42,0.08)',
  },
  chipsRight: { display: 'flex', gap: 6, alignItems: 'center' },
  toggleBtn: {
    width: 32,
    height: 32,
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    fontSize: 18,
    lineHeight: 1,
    cursor: 'pointer',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  chip: {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 999,
    padding: '6px 14px',
    fontSize: 13,
    color: '#475569',
    cursor: 'pointer',
    fontWeight: 500,
  },
  chipActive: {
    background: '#2563eb',
    color: '#fff',
    borderColor: '#2563eb',
  },
  muted: { color: '#94a3b8', fontSize: 14, marginTop: 24 },
  mentionRow: {
    display: 'flex',
    alignItems: 'stretch',
    gap: 10,
  },
  readToggle: {
    background: 'transparent',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    width: 40,
    fontSize: 22,
    lineHeight: 1,
    cursor: 'pointer',
    flexShrink: 0,
  },
};
